import { Formik, FormikActions, FormikProps } from 'formik';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { RenderChallenge } from '../../../utils/challengerMain';
import { COLORS, FONT_FAMILY, MQS } from '../constants/styles';
import Button from './Button';
import ButtonOutline from './ButtonOutline';

export interface IFormProps<T> {
    id?: string;
    renderFormFields?: (values: T) => ReactNode;
    value: T;
    handleSubmit: (values: T, actions: FormikActions<T>) => any;
    loading: boolean;
    saveButtonProps?: any;
    showInvalidFieldsNotification?: boolean;
    showWarning?: (message: ReactNode) => any;
    qaId?: string;
    challengerPid?: string;
    challengerReadyCallback?: (ready: boolean) => void;
    onCloseFunction: () => void;
}

interface IFormState<T> {
    isSubmitting: boolean;
    isChallengeLoaded: boolean;
}

const Actions = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 40px;

    & > button:first-child {
        border-color: ${COLORS.rakutenRed};
        color: ${COLORS.rakutenRed};
        margin: 0;
    }

    & > button {
        max-width: 45%;
        width: 200px;
    }
`;

const FormWrapper = styled.div`
    margin-top: 16px;
    position: relative;

    & > div:last-child {
        margin-top: 24px;
    }
`;

const ChallengerWrapper = styled.div`
    input.c-input {
        background-color: transparent;
        border: none;
        border-bottom: 1px solid;
        border-bottom-color: ${COLORS.grey3};
        border-radius: 0;
        color: ${COLORS.dark};
        display: block;
        ${FONT_FAMILY.multipleRegularCss};
        font-weight: 300;
        padding: 1px 1px 5px 1px;
        width: 100%;

        :placeholder-shown {
            padding-left: 0px;

            @media (min-width: ${MQS.mq1Min}) {
                font-size: 0.875rem;
            }

            @media (min-width: ${MQS.mq2Max}) {
                font-size: 1rem;
            }
        }

        @media (max-width: ${MQS.mq2Min}) {
            padding-left: 0;
        }

        :focus {
            outline: none;
            border-bottom: 2px solid ${COLORS.rakutenRed};
        }

        [disabled] {
            opacity: 1; // required for iOS safari;
            -webkit-text-fill-color: ${COLORS.grey3};
        }
    }
`;

class FormEkyc<T> extends React.Component<IFormProps<T>, IFormState<T>> {
    constructor(props: IFormProps<T>) {
        super(props);

        const { loading, challengerPid } = this.props;

        this.state = {
            isChallengeLoaded: !challengerPid,
            isSubmitting: loading,
        };

        this.renderForm = this.renderForm.bind(this);
        this.onChallengeReady = this.onChallengeReady.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<IFormProps<T>>): void {
        const { loading } = this.props;
        if (loading !== prevProps.loading) {
            this.setState({ ...this.state, isSubmitting: loading });
        }
    }

    private onChallengeReady = (ready: boolean) => {
        if (this.props.challengerReadyCallback) {
            this.props.challengerReadyCallback(ready);
        }
        this.setState({ ...this.state, isChallengeLoaded: ready });
    };

    public render() {
        const { value, handleSubmit } = this.props;
        return (
            <Formik
                enableReinitialize={true}
                initialValues={value}
                onSubmit={handleSubmit}
                render={this.renderForm}
            />
        );
    }

    private renderChallenge(challengerPid: string) {
        return (
            <ChallengerWrapper>
                <RenderChallenge
                    cpid={challengerPid}
                    isReadyCallback={this.onChallengeReady}
                    refreshOnDerender={true}
                />
            </ChallengerWrapper>
        );
    }

    private renderForm({ submitForm }: FormikProps<T>): ReactNode {
        const { id, saveButtonProps, onCloseFunction, qaId, challengerPid, children } = this.props;
        const submit = () => {
            this.setState({ ...this.state, isSubmitting: true });
            submitForm();
        };

        return (
            <FormWrapper id={id} data-qa-id={`${qaId}-form`}>
                {children}
                {challengerPid && this.renderChallenge(challengerPid)}
                <Actions>
                    <ButtonOutline
                        id="confirm"
                        type="button"
                        text={TranslationHelpers.getMessageDescriptor('button.unlink')}
                        onClick={submit}
                        data-qa-id="confirm-action"
                        {...saveButtonProps}
                    />
                    <Button
                        id="cancel"
                        type="button"
                        primary={true}
                        text={TranslationHelpers.getMessageDescriptor('button.cancel')}
                        onClick={onCloseFunction}
                        disabled={false}
                        success={false}
                        data-qa-id="cancel-action"
                    />
                </Actions>
            </FormWrapper>
        );
    }
}

export default FormEkyc;
